import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Initials for the round avatar: "Tunde Ade" -> "TA", "Tunde" -> "T", no name -> first letter of the email
function initialsFor(profile, user) {
  const first = profile?.first_name?.trim();
  const last = profile?.last_name?.trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first[0].toUpperCase();
  return (user?.email?.[0] ?? "?").toUpperCase();
}

export default function ProfileMenu({ user, profile, onSignOut }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");

  // Close on a click or tap outside the menu, and on Escape
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    try {
      await onSignOut();
    } finally {
      navigate("/");
    }
  }

  return (
    <div className="pm" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="pm-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${fullName || user.email}`}
        onClick={() => setOpen((o) => !o)}
      >
        {/* When customers can upload a photo, store its URL in profiles.avatar_url and it shows here */}
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt="" />
        ) : (
          <span aria-hidden="true">{initialsFor(profile, user)}</span>
        )}
      </button>

      {open && (
        <div className="pm-panel" role="menu">
          <div className="pm-head">
            <small>Signed in as</small>
            {fullName && <strong>{fullName}</strong>}
            <span>{user.email}</span>
          </div>

          <Link
            to="/update-password?mode=change"
            className="pm-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            Change password
          </Link>

          {/* Later: add "Personal details" here, e.g.
          <Link to="/account/details" className="pm-item" role="menuitem" onClick={() => setOpen(false)}>
            Personal details
          </Link> */}

          <div className="pm-sep" role="separator" />

          <button
            type="button"
            className="pm-item pm-item--danger"
            role="menuitem"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}